'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState, useRef } from 'react';
import { careerData } from '@/data/career';
import { projects } from '@/data/projects';
import Link from 'next/link';
import EditorialReveal from '@/components/EditorialReveal';
import BackgroundMarkers from '@/components/BackgroundMarkers';
import { useScroll } from '@/components/ScrollProvider';
import Image from 'next/image';
import dynamic from 'next/dynamic';
import { ProfileGlitch } from '@/components/ProfileGlitch';
const SkillsGrid = dynamic(() => import('@/components/SkillsGrid').then(m => m.SkillsGrid), {
  ssr: false
});
const OpenSourceShowcase = dynamic(() => import('@/components/OpenSourceShowcase').then(m => m.OpenSourceShowcase), {
  ssr: false
});
const TestimonialsCarousel = dynamic(() => import('@/components/TestimonialsCarousel').then(m => m.TestimonialsCarousel), {
  ssr: false
});
const ContactForm = dynamic(() => import('@/components/ContactForm'), {
  ssr: false
});
const SocialLinks = dynamic(() => import('@/components/SocialLinks'), {
  ssr: false
});
const StrategyCallCTA = dynamic(() => import('@/components/StrategyCallCTA').then(m => m.StrategyCallCTA), {
  ssr: false
});

// Dynamically import the heavy 3D scene
// loading: () => null prevents flash of unstyled content or heavy loading state, 
// keeping the background markers visible
const HomeScene = dynamic(() => import('@/components/HomeScene'), {
  ssr: false
});
export default function Home() {
  const $ = _c(91);
  const {
    setActiveCredential
  } = useScroll();
  const skillsSectionRef = useRef(null);
  const labSectionRef = useRef(null);
  const vaultSectionRef = useRef(null);
  const contactSectionRef = useRef(null);
  const [skillsProgress, setSkillsProgress] = useState(0);
  const [skillsExitProgress, setSkillsExitProgress] = useState(0);
  const [labProgress, setLabProgress] = useState(0);
  const [vaultProgress, setVaultProgress] = useState(0);
  const [contactProgress, setContactProgress] = useState(0);
  const [showScene, setShowScene] = useState(false);
  const [materializeStage, setMaterializeStage] = useState("idle");
  const sequenceStarted = useRef(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      if (sequenceStarted.current) {
        return;
      }
      sequenceStarted.current = true;
      const sequence = async () => {
        setMaterializeStage("idle");
        setShowScene(true);
        await new Promise(_temp);
        setMaterializeStage("spark");
        await new Promise(_temp2);
        setMaterializeStage("cloud");
        await new Promise(_temp3);
        setMaterializeStage("scan");
        await new Promise(_temp4);
        setMaterializeStage("complete");
      };
      sequence();
      const handleScrollProgress = () => {
        if (skillsSectionRef.current) {
          const rect = skillsSectionRef.current.getBoundingClientRect();
          const sectionHeight = rect.height;
          const windowHeight = window.innerHeight;
          if (rect.top < windowHeight && rect.bottom > 0) {
            const p = Math.min(Math.max((windowHeight - rect.top) / (windowHeight + sectionHeight), 0), 1);
            setSkillsProgress(p);
          } else {
            if (rect.top >= windowHeight) {
              setSkillsProgress(0);
            } else {
              setSkillsProgress(1);
            }
          }
          if (rect.bottom < windowHeight) {
            const ep = Math.min(Math.max((windowHeight - rect.bottom) / windowHeight, 0), 1);
            setSkillsExitProgress(ep);
          } else {
            setSkillsExitProgress(0);
          }
        }
        if (labSectionRef.current) {
          const rect_0 = labSectionRef.current.getBoundingClientRect();
          const windowHeight_0 = window.innerHeight;
          if (rect_0.top < windowHeight_0) {
            const p_0 = Math.min(Math.max((windowHeight_0 - rect_0.top) / windowHeight_0, 0), 1);
            setLabProgress(p_0);
          } else {
            setLabProgress(0);
          }
        }
        if (vaultSectionRef.current) {
          const rect_1 = vaultSectionRef.current.getBoundingClientRect();
          const sectionHeight_0 = rect_1.height;
          const windowHeight_1 = window.innerHeight;
          if (rect_1.top < windowHeight_1 && rect_1.bottom > 0) {
            const p_1 = Math.min(Math.max((windowHeight_1 - rect_1.top) / (windowHeight_1 + sectionHeight_0), 0), 1);
            setVaultProgress(p_1);
          } else {
            if (rect_1.top >= windowHeight_1) {
              setVaultProgress(0);
            } else {
              setVaultProgress(1);
            }
          }
        }
        if (contactSectionRef.current) {
          const rect_2 = contactSectionRef.current.getBoundingClientRect();
          const windowHeight_2 = window.innerHeight;
          if (rect_2.top < windowHeight_2) {
            const p_2 = Math.min(Math.max((windowHeight_2 - rect_2.top) / windowHeight_2, 0), 1);
            setContactProgress(p_2);
          } else {
            setContactProgress(0);
          }
        }
      };
      window.addEventListener("scroll", handleScrollProgress, {
        passive: true
      });
      handleScrollProgress();
      return () => {
        window.removeEventListener("scroll", handleScrollProgress);
      };
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <BackgroundMarkers />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== contactProgress || $[4] !== labProgress || $[5] !== materializeStage || $[6] !== showScene || $[7] !== skillsExitProgress || $[8] !== skillsProgress || $[9] !== vaultProgress) {
    t3 = showScene && <HomeScene progress={skillsProgress * 2} exitProgress={skillsExitProgress} vaultProgress={vaultProgress} contactProgress={contactProgress} materializeStage={materializeStage} skillsProgress={skillsProgress} labProgress={labProgress} />;
    $[3] = contactProgress;
    $[4] = labProgress;
    $[5] = materializeStage;
    $[6] = showScene;
    $[7] = skillsExitProgress;
    $[8] = skillsProgress;
    $[9] = vaultProgress;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== t3) {
    t4 = <div className="fixed inset-0 z-0 pointer-events-none">{t2}{t3}</div>;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <p className="font-mono text-[13px] tracking-[0.3em] uppercase text-zinc-400 mb-3 animate-reveal">Wenceslaus Dsilva</p>;
    t6 = <div className="inline-block px-4 py-1.5 rounded-full glass mb-8 font-mono text-[11px] tracking-[0.4em] uppercase text-primary animate-reveal">Chief Technology Officer</div>;
    $[13] = t5;
    $[14] = t6;
  } else {
    t5 = $[13];
    t6 = $[14];
  }
  let t7;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="mb-8 animate-reveal hidden" style={{
      animationDelay: "0.1s"
    }}><ProfileGlitch src="/images/hero/profile.jpg" alt="Wenceslaus Dsilva - CTO & Architect" glitchIntensity={8} glitchFrequency={0.15} /></div>;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <h1 className="text-[15vw] md:text-[12rem] leading-[0.8] tracking-tighter font-serif italic mb-12">The Visionary <br /><span className="text-primary pr-4">Architect.</span></h1>;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t10;
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      animationDelay: "0.2s"
    };
    t10 = <p className="text-xl md:text-2xl font-light text-zinc-400 leading-relaxed font-sans">20+ years of pioneering technical excellence and strategic leadership at the intersection of high-scale engineering and business innovation.</p>;
    $[17] = t10;
    $[18] = t9;
  } else {
    t10 = $[17];
    t9 = $[18];
  }
  let t11;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="max-w-6xl">{t5}{t6}{t7}{t8}<div className="grid grid-cols-1 md:grid-cols-2 gap-12 text-left max-w-4xl mx-auto animate-reveal" style={t9}>{t10}<div className="flex flex-col justify-end border-l border-white/10 pl-8"><p className="font-mono text-[11px] uppercase tracking-widest text-zinc-400 mb-2">Current Epoch</p><p className="text-xl text-zinc-200 font-serif italic">Driving Scalable Growth @ Rooftop</p></div></div></div>;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <section id="hero" className="relative flex min-h-screen w-full flex-col items-center justify-center px-8 text-center bg-transparent">{t11}<div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-4"><div className="h-24 w-px bg-gradient-to-b from-primary/50 to-transparent" /><span className="vertical-text text-[11px] font-mono uppercase tracking-[0.5em] text-zinc-400">Explore Journey</span></div></section>;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div id="epochs" className="relative pb-[20vh]"><div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-primary/20 via-primary/5 to-transparent -translate-x-1/2 z-0 hidden md:block" />{careerData.map(_temp6)}</div>;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      isolation: "isolate"
    };
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  let t16;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div className="absolute inset-0 tech-grid opacity-15 pointer-events-none" />;
    t16 = <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(99,102,241,0.08)_0%,transparent_70%)] pointer-events-none" />;
    $[23] = t15;
    $[24] = t16;
  } else {
    t15 = $[23];
    t16 = $[24];
  }
  let t17;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <EditorialReveal direction="down"><h2 className="text-6xl md:text-[8rem] font-serif italic leading-none opacity-5 uppercase tracking-tighter pointer-events-none">Ecosystem.</h2></EditorialReveal>;
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  let t18;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <section id="skills" ref={skillsSectionRef} className="md:min-h-[150vh] min-h-screen flex flex-col items-center px-8 relative bg-transparent" style={t14}><div className="md:sticky md:top-0 md:h-screen w-full flex flex-col items-center justify-center pointer-events-none z-40 relative">{t15}{t16}<div className="max-w-7xl w-full flex flex-col justify-start p-8 md:p-24 z-10 pointer-events-none overflow-visible">{t17}<div className="pointer-events-auto py-6 pb-32"><SkillsGrid /></div></div></div></section>;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  let t19;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <section id="lab" ref={labSectionRef} className="relative py-32 md:py-24 px-6 md:px-8 flex flex-col items-center bg-transparent"><div className="max-w-7xl w-full"><OpenSourceShowcase /></div></section>;
    $[27] = t19;
  } else {
    t19 = $[27];
  }
  let t20;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <EditorialReveal direction="down"><span className="font-mono text-[11px] text-primary uppercase tracking-[0.5em] mb-4 block">The Vault</span><h2 className="text-6xl md:text-[8rem] font-serif italic leading-none text-white uppercase tracking-tighter mb-12">Credentials.</h2></EditorialReveal>;
    $[28] = t20;
  } else {
    t20 = $[28];
  }
  let t21;
  let t22;
  let t23;
  let t24;
  if ($[29] !== setActiveCredential) {
    t21 = () => setActiveCredential("edu");
    t22 = () => setActiveCredential(null);
    t23 = () => setActiveCredential("edu");
    t24 = () => setActiveCredential(null);
    $[29] = setActiveCredential;
    $[30] = t21;
    $[31] = t22;
    $[32] = t23;
    $[33] = t24;
  } else {
    t21 = $[30];
    t22 = $[31];
    t23 = $[32];
    t24 = $[33];
  }
  let t25;
  let t26;
  let t27;
  let t28;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <p aria-hidden="true" className="text-zinc-400 group-hover:text-primary transition-colors text-sm font-mono mb-2 uppercase tracking-widest">Education</p>;
    t26 = <h3 className="text-2xl text-white font-serif italic mb-4">B.Sc Information Technology</h3>;
    t27 = <div aria-hidden="true" className="h-px w-12 bg-white/10 mb-4 group-hover:w-full transition-all duration-500" />;
    t28 = <p aria-hidden="true" className="text-zinc-400 text-xs uppercase tracking-tighter">St. Andrews College, Mumbai University</p>;
    $[34] = t25;
    $[35] = t26;
    $[36] = t27;
    $[37] = t28;
  } else {
    t25 = $[34];
    t26 = $[35];
    t27 = $[36];
    t28 = $[37];
  }
  let t29;
  if ($[38] !== t21 || $[39] !== t22 || $[40] !== t23 || $[41] !== t24) {
    t29 = <div tabIndex={0} onMouseEnter={t21} onMouseLeave={t22} onFocus={t23} onBlur={t24} className="glass p-8 rounded-3xl opacity-80 hover:opacity-100 focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-primary outline-none transition-all pointer-events-auto group cursor-pointer" aria-label={"B.Sc Information Technology \u2014 St. Andrews College, Mumbai University"} role="button">{t25}{t26}{t27}{t28}</div>;
    $[38] = t21;
    $[39] = t22;
    $[40] = t23;
    $[41] = t24;
    $[42] = t29;
  } else {
    t29 = $[42];
  }
  let t30;
  let t31;
  let t32;
  let t33;
  if ($[43] !== setActiveCredential) {
    t30 = () => setActiveCredential("cert1");
    t31 = () => setActiveCredential(null);
    t32 = () => setActiveCredential("cert1");
    t33 = () => setActiveCredential(null);
    $[43] = setActiveCredential;
    $[44] = t30;
    $[45] = t31;
    $[46] = t32;
    $[47] = t33;
  } else {
    t30 = $[44];
    t31 = $[45];
    t32 = $[46];
    t33 = $[47];
  }
  let t34;
  let t35;
  let t36;
  let t37;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <p aria-hidden="true" className="text-zinc-400 group-hover:text-primary transition-colors text-sm font-mono mb-2 uppercase tracking-widest">Certification</p>;
    t35 = <h3 className="text-2xl text-white font-serif italic mb-4">Elasticsearch Engineer</h3>;
    t36 = <div aria-hidden="true" className="h-px w-12 bg-white/10 mb-4 group-hover:w-full transition-all duration-500" />;
    t37 = <p aria-hidden="true" className="text-zinc-400 text-xs uppercase tracking-tighter">Elite specialized engineering certification.</p>;
    $[48] = t34;
    $[49] = t35;
    $[50] = t36;
    $[51] = t37;
  } else {
    t34 = $[48];
    t35 = $[49];
    t36 = $[50];
    t37 = $[51];
  }
  let t38;
  if ($[52] !== t30 || $[53] !== t31 || $[54] !== t32 || $[55] !== t33) {
    t38 = <div tabIndex={0} onMouseEnter={t30} onMouseLeave={t31} onFocus={t32} onBlur={t33} className="glass p-8 rounded-3xl opacity-80 hover:opacity-100 focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-primary outline-none transition-all pointer-events-auto group cursor-pointer" aria-label={"Elasticsearch Engineer \u2014 Elasticsearch Certified Engineer"} role="button">{t34}{t35}{t36}{t37}</div>;
    $[52] = t30;
    $[53] = t31;
    $[54] = t32;
    $[55] = t33;
    $[56] = t38;
  } else {
    t38 = $[56];
  }
  let t39;
  let t40;
  let t41;
  let t42;
  if ($[57] !== setActiveCredential) {
    t39 = () => setActiveCredential("cert2");
    t40 = () => setActiveCredential(null);
    t41 = () => setActiveCredential("cert2");
    t42 = () => setActiveCredential(null);
    $[57] = setActiveCredential;
    $[58] = t39;
    $[59] = t40;
    $[60] = t41;
    $[61] = t42;
  } else {
    t39 = $[58];
    t40 = $[59];
    t41 = $[60];
    t42 = $[61];
  }
  let t43;
  let t44;
  let t45;
  let t46;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = <p aria-hidden="true" className="text-zinc-400 group-hover:text-primary transition-colors text-sm font-mono mb-2 uppercase tracking-widest">Certification</p>;
    t44 = <h3 className="text-2xl text-white font-serif italic mb-4">Google Cloud Professional</h3>;
    t45 = <div aria-hidden="true" className="h-px w-12 bg-white/10 mb-4 group-hover:w-full transition-all duration-500" />;
    t46 = <p aria-hidden="true" className="text-zinc-400 text-xs uppercase tracking-tighter">{"Cloud Infrastructure & Solution Architecting."}</p>;
    $[62] = t43;
    $[63] = t44;
    $[64] = t45;
    $[65] = t46;
  } else {
    t43 = $[62];
    t44 = $[63];
    t45 = $[64];
    t46 = $[65];
  }
  let t47;
  if ($[66] !== t39 || $[67] !== t40 || $[68] !== t41 || $[69] !== t42) {
    t47 = <div tabIndex={0} onMouseEnter={t39} onMouseLeave={t40} onFocus={t41} onBlur={t42} className="glass p-8 rounded-3xl opacity-80 hover:opacity-100 focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-primary outline-none transition-all pointer-events-auto group cursor-pointer" aria-label={"Google Cloud Professional \u2014 GCP Professional Architect"} role="button">{t43}{t44}{t45}{t46}</div>;
    $[66] = t39;
    $[67] = t40;
    $[68] = t41;
    $[69] = t42;
    $[70] = t47;
  } else {
    t47 = $[70];
  }
  let t48;
  if ($[71] !== t29 || $[72] !== t38 || $[73] !== t47) {
    t48 = <section id="vault" ref={vaultSectionRef} className="md:min-h-[200vh] min-h-screen flex flex-col items-center px-8 relative bg-transparent"><div className="md:sticky md:top-0 md:h-screen w-full flex flex-col items-center justify-center pointer-events-none relative"><div className="max-w-7xl w-full text-center z-10 pointer-events-none">{t20}<div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-12 mt-12 md:mt-24 px-6 md:px-0">{t29}{t38}{t47}</div></div></div></section>;
    $[71] = t29;
    $[72] = t38;
    $[73] = t47;
    $[74] = t48;
  } else {
    t48 = $[74];
  }
  let t49;
  if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = <section id="signals" className="relative py-32 md:py-24 px-6 md:px-8 flex flex-col items-center bg-transparent"><div className="max-w-7xl w-full"><TestimonialsCarousel /></div></section>;
    $[75] = t49;
  } else {
    t49 = $[75];
  }
  let t50;
  if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
    t50 = <br />;
    $[76] = t50;
  } else {
    t50 = $[76];
  }
  let t51;
  if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
    t51 = <EditorialReveal direction="up" className="mb-8"><h2 className="text-5xl md:text-7xl font-serif italic tracking-tighter leading-tight">Let's {t50} <span className="text-primary pr-4">Connect.</span></h2></EditorialReveal>;
    $[77] = t51;
  } else {
    t51 = $[77];
  }
  let t52;
  if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
    t52 = <div className="max-w-4xl w-full flex flex-col items-center gap-8"><StrategyCallCTA /><ContactForm /><SocialLinks /></div>;
    $[78] = t52;
  } else {
    t52 = $[78];
  }
  let t53;
  let t54;
  let t55;
  let t56;
  let t57;
  let t58;
  if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = <a href="/about" className="hover:text-zinc-300 transition-colors">About</a>;
    t54 = <span className="mx-3">·</span>;
    t55 = <a href="/contact" className="hover:text-zinc-300 transition-colors">Contact</a>;
    t56 = <span className="mx-3">·</span>;
    t57 = <a href="/privacy" className="hover:text-zinc-300 transition-colors">Privacy Policy</a>;
    t58 = <span className="mx-3">·</span>;
    $[79] = t53;
    $[80] = t54;
    $[81] = t55;
    $[82] = t56;
    $[83] = t57;
    $[84] = t58;
  } else {
    t53 = $[79];
    t54 = $[80];
    t55 = $[81];
    t56 = $[82];
    t57 = $[83];
    t58 = $[84];
  }
  let t59;
  if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
    t59 = <section id="contact" ref={contactSectionRef} className="min-h-screen flex flex-col items-center justify-center px-6 md:px-8 text-center bg-transparent relative pointer-events-auto py-16 md:py-12">{t51}{t52}<p className="mt-12 font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-700">{t53}{t54}{t55}{t56}{t57}{t58}<span>© {new Date().getFullYear()} Wenceslaus Dsilva</span></p></section>;
    $[85] = t59;
  } else {
    t59 = $[85];
  }
  let t60;
  if ($[86] !== t48) {
    t60 = <div className="relative z-10">{t12}{t13}{t18}{t19}{t48}{t49}{t59}</div>;
    $[86] = t48;
    $[87] = t60;
  } else {
    t60 = $[87];
  }
  let t61;
  if ($[88] !== t4 || $[89] !== t60) {
    t61 = <main className="relative min-h-screen text-white selection:bg-primary/30">{t4}{t60}</main>;
    $[88] = t4;
    $[89] = t60;
    $[90] = t61;
  } else {
    t61 = $[90];
  }
  return t61;
}
function _temp6(milestone, index) {
  return <section key={index} className="flex min-h-screen w-full flex-col items-center justify-center px-8 md:px-24 bg-transparent relative"><div className={`w-full flex ${index % 2 === 0 ? "justify-end" : "justify-start"}`}><div className="relative max-w-4xl w-full"><div className="absolute -top-24 -left-12 opacity-[0.05] text-[15rem] md:text-[25rem] font-serif italic select-none pointer-events-none leading-none">{index + 1}</div><div className="glass p-8 md:p-16 rounded-[2rem] relative overflow-hidden group cursor-pointer hover:border-primary/20 focus-within:ring-2 focus-within:ring-primary/50 outline-none transition-all pointer-events-auto"><div className="absolute inset-0 tech-grid opacity-0 group-hover:opacity-20 transition-opacity" /><div className="absolute inset-0 scanline opacity-0 group-hover:opacity-10 transition-opacity" /><div className="flex flex-wrap items-center justify-between gap-4 mb-12 relative z-10"><span className="font-mono text-[11px] text-primary uppercase tracking-[0.3em]">{milestone.year}</span><div className="h-px w-24 bg-white/10" /><span className="font-mono text-[11px] text-zinc-400 uppercase tracking-[0.3em]">{milestone.company}</span></div><EditorialReveal direction="up" className="relative z-10"><h2 className="text-5xl md:text-8xl font-serif italic text-white mb-10 leading-[0.9] tracking-tighter">{milestone.role}</h2></EditorialReveal><EditorialReveal direction="up" delay={0.2} className="relative z-10"><p className="text-xl md:text-2xl text-zinc-400 leading-relaxed mb-12 max-w-2xl font-sans font-light">{milestone.description}</p></EditorialReveal><div className="flex flex-col md:flex-row justify-between items-end gap-10 pt-12 border-t border-white/5 relative z-10"><div className="grid grid-cols-1 md:grid-cols-2 gap-6 flex-grow">{milestone.highlights?.slice(0, 2).map(_temp5)}</div>{(() => {
              const project = projects.find(p_3 => milestone.company.toLowerCase().includes(p_3.company.toLowerCase()) || p_3.company.toLowerCase().includes(milestone.company.toLowerCase()));
              return project && <Link href={`/work/${project.slug}`} className="inline-flex items-center gap-2 group/btn pointer-events-auto focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none rounded-full p-1 cursor-pointer" aria-label={`Read Case Study — ${project.company}`} title={`View ${project.company} case study`}><span className="font-mono text-[11px] uppercase tracking-widest text-zinc-400 group-hover/btn:text-primary transition-colors">Read Case Study</span><div aria-hidden="true" className="size-10 rounded-full border border-white/10 flex items-center justify-center group-hover/btn:border-primary/50 transition-colors text-zinc-400 group-hover/btn:text-primary">→</div></Link>;
            })()}</div></div></div></div></section>;
}
function _temp5(h, i) {
  return <div key={i} className="flex gap-4"><span className="text-primary font-mono text-xs">/0{i + 1}</span><p className="text-sm text-zinc-300 font-light leading-snug">{h}</p></div>;
}
function _temp4(resolve_2) {
  return setTimeout(resolve_2, 2000);
}
function _temp3(resolve_1) {
  return setTimeout(resolve_1, 2500);
}
function _temp2(resolve_0) {
  return setTimeout(resolve_0, 1500);
}
function _temp(resolve) {
  return setTimeout(resolve, 500);
}